// ============================================
// Path: /frontend/src/pages/CheckoutPage.jsx
// UPDATED: Skip tier selection if user already has a tier and is within limits
// CSS OPTIMIZED: Mobile-first responsive design
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useToast } from '../hooks/useToast';
import { checkUserCanCreateListing } from '../utils/tierCheck';
import api from '../api/client';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import { Loader2, CheckCircle, ArrowRight, ChevronDown } from 'lucide-react';

const plans = [
  {
    name: 'Bronze',
    listings: 10,
    price: 2650,
    duration: '1 Month',
    description: 'List up to 10 cars.',
    features: [
      'List up to 100 Cars/Properties',
      'Comprehensive user dashboard',
      'Chat on WhatsApp on all listings',
      'Share on WhatsApp on all listings',
      'WhatsApp Bulk Share posting',
      'Analytic Tools',
      '50x visibility'
    ]
  },
  {
    name: 'Silver',
    listings: 40,
    price: 5350,
    duration: '1 Month',
    description: 'List up to 50 cars.',
    features: [
      'List up to 100 Cars/Properties',
      'Comprehensive user dashboard',
      'Chat on WhatsApp on all listings',
      'Share on WhatsApp on all listings',
      'WhatsApp Bulk Share posting',
      'Analytic Tools',
      '100x visibility'
    ]
  },
  {
    name: 'Gold',
    listings: 100,
    price: 8450,
    duration: '1 Month',
    description: 'List up to 100 cars & get a car yard display.',
    features: [
      'List up to 100 Cars/Properties',
      'Comprehensive user dashboard',
      'Chat on WhatsApp on all listings',
      'Share on WhatsApp on all listings',
      'WhatsApp Bulk Share posting',
      'Facebook bulk sharing',
      'Analytic Tools',
      'Access to featured car yards or real estate agents',
      'Access to home page featured listings',
      '150x visibility'
    ]
  },
  {
    name: 'Platinum',
    listings: 'Unlimited',
    price: 9850,
    duration: '1 Month',
    description: 'List up to 140 cars & get a car yard display.',
    features: [
      'List up to 140 Cars/Properties',
      'Comprehensive user dashboard',
      'Chat on WhatsApp on all listings',
      'Share on WhatsApp on all listings',
      'WhatsApp Bulk Share posting',
      'Facebook bulk sharing',
      'Analytic Tools',
      'Access to featured car yards or real estate agents',
      'Access to home page featured listings',
      '180x visibility'
    ]
  },
];

export default function CheckoutPage() {
  console.log('🎨 [CheckoutPage] Component mounted');

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [pendingListing, setPendingListing] = useState(null);
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const hasCreatedRef = useRef(false);
  const [expandedPlan, setExpandedPlan] = useState(null);


useEffect(() => {
  if (hasCreatedRef.current) return;
  console.log('🔵 [CheckoutPage] Checking for pending listing...');

  // ✅ NEW: Check if this is a reactivation flow (not a new listing)
  const reactivatingListingId = sessionStorage.getItem('reactivatingListingId');
  
  if (reactivatingListingId) {
    console.log('✅ [CheckoutPage] Reactivation flow detected for listing:', reactivatingListingId);
    // Set a dummy pending listing so the page knows we have something to process
    setPendingListing({ isReactivation: true, listingId: reactivatingListingId });
    return; // Just show tier selection, don't redirect
  }

  // ✅ NEW: Check if this is a subscription renewal flow
  const renewingSubscription = sessionStorage.getItem('renewingSubscription');

  if (renewingSubscription) {
    console.log('✅ [CheckoutPage] Renewal flow detected');
    setPendingListing({ isRenewal: true });
    return; // Just show tier selection, don't redirect
  }

  // Original flow for new listings
  const listingData = sessionStorage.getItem('pendingListing');

  if (!listingData) {
    console.log('⚠️ [CheckoutPage] No pending listing found');
    toast({
      variant: 'destructive',
      title: 'No Listing Data',
      description: 'Please create a listing first.',
    });
    navigate('/listings/create');
    return;
  }

  const parsed = JSON.parse(listingData);
  
  // ⭐ STEP 2 FIX: Add image previews from form data
  const formData = sessionStorage.getItem('createListingFormData');
  if (formData) {
    const form = JSON.parse(formData);
    parsed.imagePreviews = form.imagePreviews; // Carry over the base64 previews
    console.log('✅ [CheckoutPage] Added image previews from form data');
  }
  
  console.log('✅ [CheckoutPage] Pending listing loaded:', parsed);
  setPendingListing(parsed);

  // Check if user can create listing without payment
  if (user) {
    console.log('🔵 [CheckoutPage] Checking user tier limits...');
    
    const tierCheck = checkUserCanCreateListing(user);
    console.log('📊 [CheckoutPage] Tier check result:', tierCheck);
    
    if (tierCheck.canCreate) {
      // User has active subscription with remaining ads - skip payment
      console.log('✅ [CheckoutPage] User within limits, creating listing directly');
      hasCreatedRef.current = true;
      createListingDirectly(parsed);
      return; // Exit early - don't show tier selection
    } else {
      // User needs to pay (expired, exceeded limits, or no subscription)
      console.log('⚠️ [CheckoutPage] User needs payment:', tierCheck.reason);
      // Continue to show tier selection below
    }
  }
}, [user, navigate, toast]);


  // ⭐ NEW: Create listing directly without payment (for existing subscribers within limits)
  const createListingDirectly = async (listingData) => {
    setIsCreatingListing(true);

    try {
      console.log('🔵 [CheckoutPage] Creating listing directly...');

      const response = await api.post('/listings', listingData);

      if (response.data.success) {
        console.log('✅ [CheckoutPage] Listing created successfully');

        // Clear pending listing
        sessionStorage.removeItem('pendingListing');
        sessionStorage.removeItem('createListingFormData');

        toast({
          title: 'Listing Created!',
          description: 'Your listing has been submitted for approval.',
        });

        // Redirect to my listings
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (error) {
      console.error('❌ [CheckoutPage] Failed to create listing:', error);

      // Check if it's a tier limit error
      if (error.response?.data?.tierLimitExceeded) {
        toast({
          variant: 'destructive',
          title: 'Tier Limit Exceeded',
          description: error.response.data.message,
        });
        setIsCreatingListing(false);
        // Show tier selection for upgrade
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.response?.data?.message || 'Failed to create listing. Please try again.',
        });
        setIsCreatingListing(false);
      }
    }
  };

  const handlePlanSelect = (plan) => {
    console.log('👤 [CheckoutPage] Plan selected:', plan);
    setSelectedPlan(plan);
  };

  const handleProceedToPayment = () => {
    console.log('👤 [CheckoutPage] User clicked proceed to payment');

    if (!selectedPlan || !pendingListing) {
      console.log('⚠️ [CheckoutPage] Missing plan or listing data');
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please select a plan and ensure listing data is available.',
      });
      return;
    }

    console.log('🔵 [CheckoutPage] Storing selected plan...', selectedPlan);
    sessionStorage.setItem('selectedPlan', JSON.stringify(selectedPlan));

    console.log('🔵 [CheckoutPage] Navigating to payment page...');
    navigate('/listings/payment');
  };

  if (authLoading || !user) {
    console.log('⏳ [CheckoutPage] Loading auth...');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  // ⭐ NEW: Show loading state while creating listing directly
  if (isCreatingListing) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center px-4">
          <Card className="max-w-md mx-auto w-full">
            <CardContent className="pt-4 sm:pt-6 text-center space-y-3 sm:space-y-4 p-4 sm:p-6">
              <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin mx-auto text-primary" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Creating Your Listing</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Please wait...</p>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  console.log('🎨 [CheckoutPage] Rendering with state:', {
    selectedPlan,
    hasPendingListing: !!pendingListing,
  });

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {/* CSS FIX 1: Main container padding - py-12 md:py-20 → py-6 sm:py-8 md:py-12 + px-4 */}
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* CSS FIX 2: Card max-width and margins */}
        <Card className="max-w-4xl mx-auto">
          {/* CSS FIX 3: CardHeader padding and text alignment */}
          <CardHeader className="text-center p-4 sm:p-5 md:p-6">
            {/* CSS FIX 4: CardTitle sizing - text-3xl → text-xl sm:text-2xl md:text-3xl */}
            <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold">
              Choose Your Plan
            </CardTitle>
            {/* CSS FIX 5: CardDescription sizing - default → text-xs sm:text-sm md:text-base */}
            <CardDescription className="text-xs sm:text-sm md:text-base mt-1.5 sm:mt-2">
              Select a subscription plan to publish your listing. All plans are
              billed monthly.
            </CardDescription>
          </CardHeader>

          {/* CSS FIX 6: CardContent padding - p-3 sm:p-6 → p-3 sm:p-4 md:p-6 */}
          <CardContent className="p-3 sm:p-4 md:p-6">
            {/* CSS FIX 7: Plans container gap - gap-3 → gap-2 sm:gap-3 */}
            <div className="flex flex-col gap-2 sm:gap-3">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`bg-white border-2 rounded-lg transition-all ${
                    selectedPlan?.name === plan.name
                      ? 'border-primary shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Compact Header - Always Visible */}
                  {/* CSS FIX 8: Button padding - p-3 sm:p-4 → p-2.5 sm:p-3 md:p-4 */}
                  <button
                    onClick={() => {
                      handlePlanSelect(plan);
                      setExpandedPlan(expandedPlan === plan.name ? null : plan.name);
                    }}
                    className="w-full p-2.5 sm:p-3 md:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
                  >
                    {/* CSS FIX 9: Inner flex gap - gap-3 → gap-2 sm:gap-3 */}
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      {/* Category */}
                      <div className="flex-shrink-0">
                        {/* CSS FIX 10: Plan name sizing - text-sm sm:text-base → text-xs sm:text-sm md:text-base */}
                        <span className="text-xs sm:text-sm md:text-base font-bold text-gray-900">
                          {plan.name}
                        </span>
                      </div>
                      
                      {/* Price */}
                      <div className="flex-shrink-0">
                        {/* CSS FIX 11: Price sizing - text-sm sm:text-base → text-xs sm:text-sm md:text-base */}
                        <span className="text-xs sm:text-sm md:text-base font-bold text-primary">
                          KES {plan.price.toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Duration */}
                      <div className="hidden sm:block flex-shrink-0">
                        {/* CSS FIX 12: Duration sizing - text-xs sm:text-sm → text-[10px] sm:text-xs md:text-sm */}
                        <span className="text-[10px] sm:text-xs md:text-sm text-gray-600">
                          {plan.duration}
                        </span>
                      </div>
                      
                      {/* Listings */}
                      <div className="flex-shrink-0">
                        {/* CSS FIX 13: Listings sizing - text-xs sm:text-sm → text-[10px] sm:text-xs md:text-sm */}
                        <span className="text-[10px] sm:text-xs md:text-sm text-gray-600">
                          {plan.listings} {typeof plan.listings === 'number' ? 'listings' : ''}
                        </span>
                      </div>
                    </div>
                    
                    {/* Icons */}
                    {/* CSS FIX 14: Icons gap - gap-2 → gap-1.5 sm:gap-2 */}
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      {selectedPlan?.name === plan.name && (
                        // CSS FIX 15: CheckCircle icon - w-4 h-4 sm:w-5 sm:h-5 → w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5
                        <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-primary" />
                      )}
                      {/* CSS FIX 16: ChevronDown icon - w-4 h-4 sm:w-5 sm:h-5 → w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 */}
                      <ChevronDown
                        className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-gray-400 transition-transform ${
                          expandedPlan === plan.name ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expandable Features */}
                  {expandedPlan === plan.name && (
                    // CSS FIX 17: Features container padding - p-3 sm:p-4 → p-2.5 sm:p-3 md:p-4
                    <div className="border-t border-gray-200 p-2.5 sm:p-3 md:p-4 bg-gray-50">
                      {/* CSS FIX 18: Features list spacing - space-y-2 → space-y-1.5 sm:space-y-2 */}
                      <ul className="space-y-1.5 sm:space-y-2">
                        {plan.features.map((feature, idx) => (
                          // CSS FIX 19: Feature item gap - gap-2 → gap-1.5 sm:gap-2
                          // CSS FIX 20: Feature text sizing - text-xs sm:text-sm → text-[10px] sm:text-xs md:text-sm
                          <li key={idx} className="flex items-start gap-1.5 sm:gap-2 text-[10px] sm:text-xs md:text-sm text-gray-700">
                            {/* CSS FIX 21: Feature icon - w-3 h-3 sm:w-4 sm:h-4 → w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 */}
                            <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
          
          {/* CSS FIX 22: CardFooter padding - border-t pt-6 → border-t pt-3 sm:pt-4 md:pt-6 + p-3 sm:p-4 md:p-6 */}
          <CardFooter className="flex justify-center border-t pt-3 sm:pt-4 md:pt-6 p-3 sm:p-4 md:p-6">
            {/* CSS FIX 23: Button sizing - size="lg" text-lg → custom heights and text sizes */}
            <Button
              size="lg"
              disabled={!selectedPlan}
              onClick={handleProceedToPayment}
              className="font-bold text-sm sm:text-base md:text-lg h-10 sm:h-11 md:h-12 px-4 sm:px-6 md:px-8"
            >
              Proceed to Payment <ArrowRight className="ml-1.5 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </CardFooter>
        </Card>
      </main>
      <Footer />
    </div>
  );
}